import { useState, useCallback } from 'react'

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

const reviveDates = (value) => {
  if (typeof value === 'string' && ISO_DATETIME_RE.test(value)) {
    const d = new Date(value)
    return isNaN(d.getTime()) ? value : d
  }
  if (Array.isArray(value)) return value.map(reviveDates)
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, reviveDates(v)])
    )
  }
  return value
}

const usePersistedFilters = (storageKey, defaultFilters, options = {}) => {
  const { storageType = 'session', persistPage = false, omitKeys = [] } = options

  const storage = storageType === 'local' ? localStorage : sessionStorage

  const readStorage = useCallback(() => {
    try {
      const raw = storage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        return reviveDates(parsed)
      }
    } catch (e) {
      console.warn(`[usePersistedFilters] Failed to parse storage for key "${storageKey}":`, e)
    }
    return null
  }, [storage, storageKey])

  const writeStorage = useCallback(
    (value) => {
      try {
        const persistedValue = { ...value }
        omitKeys.forEach(key => delete persistedValue[key])
        storage.setItem(storageKey, JSON.stringify(persistedValue))
      } catch (e) {
        console.warn(`[usePersistedFilters] Failed to write storage for key "${storageKey}":`, e)
      }
    },
    [omitKeys, storage, storageKey]
  )

  const clearStorage = useCallback(() => {
    try {
      storage.removeItem(storageKey)
    } catch (e) {
      console.warn(`[usePersistedFilters] Failed to remove storage for key "${storageKey}":`, e)
    }
  }, [storage, storageKey])


  const buildInitial = useCallback(() => {
    const saved = readStorage()
    if (!saved) return { ...defaultFilters }

    const merged = { ...defaultFilters, ...saved }

    if (!persistPage) {
      merged.page = defaultFilters.page ?? 1
    }

    return merged
  }, []) 

  const [filters, setFilters] = useState(buildInitial)
  const [appliedFilters, setAppliedFilters] = useState(buildInitial)

  const setFieldValue = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }, [])

  const applyFilters = useCallback((nextFilters) => {
    setFilters(prev => {
      const hasExplicitFilters =
        nextFilters &&
        typeof nextFilters === 'object' &&
        !nextFilters.nativeEvent
      const source = hasExplicitFilters ? nextFilters : prev
      const next = { ...source, page: defaultFilters.page ?? 1 }
      setAppliedFilters(next)
      writeStorage(next)
      return next
    })
  }, [defaultFilters, writeStorage])

  const resetFilters = useCallback(() => {
    const fresh = { ...defaultFilters }
    setFilters(fresh)
    setAppliedFilters(fresh)
    clearStorage()
  }, [defaultFilters, clearStorage])

  const onPageChange = useCallback((page, perPage) => {
    setFilters(prev => {
      const nextDraft = {
        ...prev,
        page,
        per_page: perPage !== undefined ? perPage : prev.per_page,
        page_size: perPage !== undefined ? perPage : prev.page_size,
        perPage: perPage !== undefined ? perPage : prev.perPage,
      }
      setAppliedFilters(current => {
        const nextApplied = {
          ...current,
          page,
          per_page: perPage !== undefined ? perPage : current.per_page,
          page_size: perPage !== undefined ? perPage : current.page_size,
          perPage: perPage !== undefined ? perPage : current.perPage,
        }
        writeStorage(nextApplied)
        return nextApplied
      })
      return nextDraft
    })
  }, [writeStorage])

  return {
    filters,
    setFilters,
    appliedFilters,
    setFieldValue,
    applyFilters,
    resetFilters,
    onPageChange,
  }
}

export default usePersistedFilters
